import { EmployeeRole } from '@/api/apis/resources';
import {
  AdminPermissions,
  ArchiveCasesPermissions,
  BulkAssignmentPermissions,
  CaseBoardPermissions,
  CaseDetailsPermissions,
  ChatViewPermissions,
  InvoicePermissions,
  MultiCaseSuggestPermissions,
  NotificationToastPermissions,
  ProfilePermissions,
  ReleaseNotesPermissions,
  RouteBuilderPermissions,
  SchedulerPermissions,
} from '@/permissions';

export type PermissionType =
  | RouteBuilderPermissions
  | CaseDetailsPermissions
  | CaseBoardPermissions
  | ArchiveCasesPermissions
  | AdminPermissions
  | SchedulerPermissions
  | InvoicePermissions
  | NotificationToastPermissions
  | ProfilePermissions
  | ChatViewPermissions
  | BulkAssignmentPermissions
  | MultiCaseSuggestPermissions
  | ReleaseNotesPermissions;

export const ROUTE_BUILDER_PERMISSIONS_BY_ROLES: Record<RouteBuilderPermissions, EmployeeRole[]> = {
  [RouteBuilderPermissions.ViewBuilder]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.Section16Officer,
  ],
  [RouteBuilderPermissions.LegCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.AviationSpecialist,
  ],
  [RouteBuilderPermissions.LegDelete]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.AirLegEdit]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.GroundLegEdit]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.GroundSpecialist,
  ],
  [RouteBuilderPermissions.AssignResource]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.AlgorithmView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.AviationSpecialist,
  ],
  [RouteBuilderPermissions.AlgorithmApply]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.AviationSpecialist,
  ],
  [RouteBuilderPermissions.LogView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionSendToAviationApprove]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionAviationApprove]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.AviationSpecialist,
  ],
  [RouteBuilderPermissions.ActionSendToGroundApprove]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionGroundApprove]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionRouteComplete]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionBookResources]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionSendQuoteInternal]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionReviewAirQuote]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.Section16Officer,
  ],
  [RouteBuilderPermissions.ActionReviewGroundQuote]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
  ],
  [RouteBuilderPermissions.ActionRecallAirQuote]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionRecallGroundQuote]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
  ],
  [RouteBuilderPermissions.ActionSendQuote]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
  ],
  [RouteBuilderPermissions.ActionApproveQuoteByTx]: [
    EmployeeRole.Admin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
  ],
  [RouteBuilderPermissions.DeviceTrackingLogView]: [EmployeeRole.TechSupportAdmin],
  [RouteBuilderPermissions.ActionLightsAndSirensView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
  ],
  [RouteBuilderPermissions.ActionLightsAndSirensHandle]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
  ],
  [RouteBuilderPermissions.ActionNonStopFlightView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
  ],
  [RouteBuilderPermissions.ActionNonStopFlightHandle]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
  ],
};

/** Base roles shared across attachment Create, Update, and Delete permissions */
const CASE_ATTACHMENTS_MANAGE_ROLES: EmployeeRole[] = [
  EmployeeRole.TechSupportAdmin,
  EmployeeRole.SuperAdmin,
  EmployeeRole.Admin,
  EmployeeRole.QuoteApprovers,
  EmployeeRole.LogisticsTeamManagement,
  EmployeeRole.OrganLeadership,
  EmployeeRole.OrganManagement,
  EmployeeRole.ClinicalAffairsLeadership,
  EmployeeRole.NationalCSManagement,
  EmployeeRole.ClinicalCoordinatorManagement,
  EmployeeRole.ClinicalCoordinator,
  EmployeeRole.ClinicalAuditor,
  EmployeeRole.RegionalCSManagement,
  EmployeeRole.OrganManagementSpecialist,
];

const KIDNEY_TRIAL_GLOBAL_ROLES: EmployeeRole[] = [
  EmployeeRole.Admin,
  EmployeeRole.SuperAdmin,
  EmployeeRole.TechSupportAdmin,
];
const KIDNEY_TRIAL_OWN_CASE_ROLES: EmployeeRole[] = [EmployeeRole.ClinicalCoordinator];

export const CASE_DETAILS_PERMISSIONS_BY_ROLES: Record<CaseDetailsPermissions, EmployeeRole[]> = {
  [CaseDetailsPermissions.CaseRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.CaseStatusUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalAuditor,
  ],
  [CaseDetailsPermissions.CaseGeneralUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
  ],
  [CaseDetailsPermissions.CaseAttachmentsRead]: [
    ...CASE_ATTACHMENTS_MANAGE_ROLES,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.CommercialAdoption,
    EmployeeRole.CommercialAdoptionManagement,
  ],
  [CaseDetailsPermissions.CaseAttachmentsCreate]: [...CASE_ATTACHMENTS_MANAGE_ROLES, EmployeeRole.SrLogisticsManager],
  [CaseDetailsPermissions.CaseAttachmentsUpdate]: [
    ...CASE_ATTACHMENTS_MANAGE_ROLES,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
  ],
  [CaseDetailsPermissions.CaseAttachmentsDelete]: CASE_ATTACHMENTS_MANAGE_ROLES,
  [CaseDetailsPermissions.CaseDonorUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
  ],
  [CaseDetailsPermissions.CaseRetrievalUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
  ],
  [CaseDetailsPermissions.CaseDAGeneralUpdate]: [EmployeeRole.ClinicalAuditor],
  [CaseDetailsPermissions.CaseDADonorUpdate]: [EmployeeRole.ClinicalAuditor],
  [CaseDetailsPermissions.CaseDARetrievalUpdate]: [EmployeeRole.ClinicalAuditor],
  [CaseDetailsPermissions.NoteUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.RegionalCSManagement,
  ],
  [CaseDetailsPermissions.AssignedResourcesUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
  ],
  [CaseDetailsPermissions.ClinicalDataRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.CaseLogsRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.SurgeonCompletionRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.ResourceStatusUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalCoordinator,
  ],
  [CaseDetailsPermissions.ResourceConsoleStatusUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.RegionalCSManagement,
  ],
  [CaseDetailsPermissions.LogisticsPanelRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.TripDetailsRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.OrgansRecoveredUpdate]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
  ],
  [CaseDetailsPermissions.OrgansRecoveredDAUpdate]: [EmployeeRole.ClinicalAuditor],
  [CaseDetailsPermissions.CasePilotsRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
  ],
  [CaseDetailsPermissions.CasePilotsPhoneNumberRead]: [EmployeeRole.AviationSpecialist],
  [CaseDetailsPermissions.CaseSyncDataCreate]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalLead,
  ],
  [CaseDetailsPermissions.CaseSyncDataRead]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalLead,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.CaseTrialWarningRead]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalLead,
  ],
  [CaseDetailsPermissions.CaseQuoteRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.FinanceAuditor,
  ],
  [CaseDetailsPermissions.CaseSCSMethodUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
  ],
  [CaseDetailsPermissions.OwnCaseScsMethodUpdate]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
  ],
  [CaseDetailsPermissions.TrialQuestionnaireRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.TrialQuestionnaireUpdate]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalCoordinator,
  ],
  [CaseDetailsPermissions.LocationTrackingRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseDetailsPermissions.LocationTrackingUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
  ],
  [CaseDetailsPermissions.OpoDonorDataUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [CaseDetailsPermissions.KidneyTrialUpdate]: KIDNEY_TRIAL_GLOBAL_ROLES,
  [CaseDetailsPermissions.OwnCaseKidneyTrialUpdate]: KIDNEY_TRIAL_OWN_CASE_ROLES,
  [CaseDetailsPermissions.KidneyRandomizationUpdate]: KIDNEY_TRIAL_GLOBAL_ROLES,
  [CaseDetailsPermissions.OwnCaseKidneyRandomizationUpdate]: KIDNEY_TRIAL_OWN_CASE_ROLES,
};

export const ADMIN_PERMISSIONS_BY_ROLES: Record<AdminPermissions, EmployeeRole[]> = {
  [AdminPermissions.TmdxUsersView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CLevel,
  ],
  [AdminPermissions.TmdxUsersUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.CLevel,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
  ],
  [AdminPermissions.TmdxUsersCSUpdate]: [
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.RegionalCSManagement,
  ],
  [AdminPermissions.TmdxUsersCSUpdateTrialProgram]: [EmployeeRole.OrganLeadership],
  [AdminPermissions.TmdxUsersCLUpdate]: [EmployeeRole.NationalCSManagement, EmployeeRole.ScheduleManagerCs],
  [AdminPermissions.TmdxUsersUpdateRoles]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [AdminPermissions.TmdxUsersAssignOfferCoordinator]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [AdminPermissions.TxCenterCreate]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [AdminPermissions.TxCenterUpdateNotes]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
  ],
  [AdminPermissions.TxCenterUpdateQadCustomerId]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.FinanceAuditor,
  ],
  [AdminPermissions.TxCenterView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
  ],
  [AdminPermissions.OpoCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.NationalCSManagement,
  ],
  [AdminPermissions.OpoView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
  ],
  [AdminPermissions.HospitalCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
  ],
  [AdminPermissions.HospitalView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
  ],
  [AdminPermissions.OcsConsolesCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.MaintenanceOfficer,
  ],
  [AdminPermissions.OcsConsolesView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.MaintenanceOfficer,
  ],
  [AdminPermissions.OcsConsolesUpdate]: [
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.MaintenanceOfficer,
  ],
  [AdminPermissions.OcsConsolesDelete]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.MaintenanceOfficer,
  ],
  [AdminPermissions.OcsPerfusionsSetsView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.MaintenanceOfficer,
  ],
  [AdminPermissions.NopHubCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.NationalCSManagement,
  ],
  [AdminPermissions.NopHubView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.NationalCSManagement,
  ],
  [AdminPermissions.NopHubRemove]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.NationalCSManagement,
  ],
  [AdminPermissions.RegionsRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.FinanceAuditor,
  ],
  [AdminPermissions.RegionsUpdate]: [
    // Disabled in scope of TMDXLP-9059
    // EmployeeRole.Admin,
    // EmployeeRole.SuperAdmin,
    // EmployeeRole.SrLogisticsManager,
    // EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [AdminPermissions.ProvidersConfigurationCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
  ],
  [AdminPermissions.ProvidersConfigurationView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
  ],
  [AdminPermissions.ProvidersConfigurationUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
  ],
  [AdminPermissions.AdminView]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.MaintenanceOfficer,
  ],
};

export const CASE_BOARD_PERMISSIONS_BY_ROLES: Record<CaseBoardPermissions, EmployeeRole[]> = {
  [CaseBoardPermissions.CaseBoardUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.CLevel,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseBoardPermissions.CaseBoardRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.CLevel,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [CaseBoardPermissions.CaseDraftCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalAuditor,
  ],
  [CaseBoardPermissions.CaseDraftUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalAuditor,
  ],
  [CaseBoardPermissions.CaseDraftDelete]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.LogisticsManager,
    EmployeeRole.ClinicalAuditor,
  ],
};

export const ARCHIVE_CASES_PERMISSIONS_BY_ROLES: Record<ArchiveCasesPermissions, EmployeeRole[]> = {
  [ArchiveCasesPermissions.ViewArchive]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [ArchiveCasesPermissions.CaseArchiveAccess90DaysRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
  ],
  [ArchiveCasesPermissions.CaseArchiveAccess90DaysCreate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
  ],
  [ArchiveCasesPermissions.CaseArchiveAccess90DaysDelete]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
  ],
  [ArchiveCasesPermissions.CaseArchiveAccessIndefiniteRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganLeadership,
  ],
  [ArchiveCasesPermissions.CaseArchiveAccessIndefiniteCreate]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagement,
  ],
  [ArchiveCasesPermissions.CaseArchiveAccessIndefiniteDelete]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagement,
  ],
};

const BASE_READ_SCHEDULER_ROLES = [
  EmployeeRole.Admin,
  EmployeeRole.SuperAdmin,
  EmployeeRole.TechSupportAdmin,
  EmployeeRole.QuoteApprovers,
  EmployeeRole.SrLogisticsManager,
  EmployeeRole.LogisticsManager,
  EmployeeRole.AviationSpecialist,
  EmployeeRole.GroundSpecialist,
  EmployeeRole.OrganLeadership,
  EmployeeRole.OrganManagementSpecialist,
  EmployeeRole.OrganManagement,
  EmployeeRole.ClinicalLead,
  EmployeeRole.ClinicalCoordinator,
  EmployeeRole.ClinicalSpecialist,
  EmployeeRole.RetrievalSurgeon,
  EmployeeRole.ScheduleManagerCs,
  EmployeeRole.ScheduleManagerRs,
  EmployeeRole.ScheduleManagerCc,
  EmployeeRole.ScheduleManagerOms,
  EmployeeRole.FinanceAuditor,
  EmployeeRole.Section16Officer,
  EmployeeRole.LogisticsTeamManagement,
  EmployeeRole.ClinicalAffairsLeadership,
  EmployeeRole.NationalCSManagement,
  EmployeeRole.ClinicalCoordinatorManagement,
  EmployeeRole.ClinicalAuditor,
  EmployeeRole.RegionalCSManagement,
];

export const SCHEDULER_PERMISSIONS_BY_ROLES: Record<SchedulerPermissions, EmployeeRole[]> = {
  [SchedulerPermissions.ScheduleRead]: BASE_READ_SCHEDULER_ROLES,
  [SchedulerPermissions.CCMyScheduleRead]: [EmployeeRole.ClinicalCoordinator],
  [SchedulerPermissions.CCMyScheduleWrite]: [EmployeeRole.ClinicalCoordinator],
  [SchedulerPermissions.CCSchedulesRead]: BASE_READ_SCHEDULER_ROLES,
  [SchedulerPermissions.CCSchedulesWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [SchedulerPermissions.CCSchedulesSettingsRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [SchedulerPermissions.CCSchedulesSettingsWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [SchedulerPermissions.CCSchedulesFixationTypeWrite]: [EmployeeRole.TechSupportAdmin],
  [SchedulerPermissions.CSSchedulesRead]: BASE_READ_SCHEDULER_ROLES,
  [SchedulerPermissions.CSSchedulesWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.NationalCSManagement,
  ],
  [SchedulerPermissions.CSSchedulesSettingsRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.NationalCSManagement,
  ],
  [SchedulerPermissions.CSSchedulesSettingsInputsWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.NationalCSManagement,
  ],
  [SchedulerPermissions.CSSchedulesSettingsRulesWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.NationalCSManagement,
  ],
  [SchedulerPermissions.CSSchedulesRestPeriodWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.NationalCSManagement,
  ],
  [SchedulerPermissions.CSSchedulesFixationTypeWrite]: [EmployeeRole.TechSupportAdmin],
  [SchedulerPermissions.SSchedulesRead]: BASE_READ_SCHEDULER_ROLES,
  [SchedulerPermissions.SSchedulesWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.OrganManagement,
  ],
  [SchedulerPermissions.SLiSchedulesWrite]: [EmployeeRole.ScheduleManagerRs],
  [SchedulerPermissions.SHtLuSchedulesWrite]: [EmployeeRole.ScheduleManagerRs],
  [SchedulerPermissions.SSchedulesSettingsRead]: BASE_READ_SCHEDULER_ROLES,
  [SchedulerPermissions.SSchedulesSettingsWrite]: [EmployeeRole.SuperAdmin, EmployeeRole.TechSupportAdmin],
  [SchedulerPermissions.SSchedulesRestPeriodWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ClinicalCoordinatorManagement,
  ],
  [SchedulerPermissions.SSchedulesFixationTypeWrite]: [EmployeeRole.TechSupportAdmin],
  [SchedulerPermissions.SLiSchedulesRestPeriodWrite]: [EmployeeRole.ScheduleManagerRs],
  [SchedulerPermissions.SHtLuSchedulesRestPeriodWrite]: [EmployeeRole.ScheduleManagerRs],
  [SchedulerPermissions.OMSSchedulesRead]: [...BASE_READ_SCHEDULER_ROLES, EmployeeRole.ContractorRetrievalSurgeon],
  [SchedulerPermissions.OMSSchedulesWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerOms,
  ],
  [SchedulerPermissions.OMSSchedulesSettingsRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerOms,
  ],
  [SchedulerPermissions.OMSSchedulesSettingsInputsWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerOms,
  ],
  [SchedulerPermissions.OMSSchedulesSettingsRulesWrite]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.ScheduleManagerOms,
  ],
  [SchedulerPermissions.OMSSchedulesFixationTypeWrite]: [EmployeeRole.TechSupportAdmin],
};

export const INVOICE_PERMISSIONS_BY_ROLES: Record<InvoicePermissions, EmployeeRole[]> = {
  [InvoicePermissions.ReadInvoice]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.FinanceAuditor,
  ],
};

export const NOTIFICATION_TOAST_PERMISSIONS_BY_ROLES: Record<NotificationToastPermissions, EmployeeRole[]> = {
  [NotificationToastPermissions.ViewToastWithEventNameCaseResourceAssigned]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusClinicalScreening]: [
    EmployeeRole.ClinicalCoordinator,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusResourcesDeployed]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusCaseDeclined]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.LogisticsManager,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.Section16Officer,
    EmployeeRole.GroundSpecialist,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusTransplantIsNotCompleted]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusCaseFinished]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusCaseFinishedAndOldStatusTransplantIsNotCompleted]:
    [EmployeeRole.ClinicalCoordinator],
  [NotificationToastPermissions.ViewToastWithEventNameOrganAcceptanceCriteriaUpdated]: [
    EmployeeRole.ClinicalCoordinator,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameOrganApprovalUpdated]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseDetailsUpdated]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.SrLogisticsManager,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameResourceStatusUpdated]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.LogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.SrLogisticsManager,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameTBD]: [
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.QuoteApprovers,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusPendingLogistics]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.GroundSpecialist,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusResourcesAssigned]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.ClinicalCoordinator,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseStatusUpdatedAndNewStatusOnOCS]: [
    EmployeeRole.Section16Officer,
    EmployeeRole.LogisticsManager,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameMAOTripCreationFailed]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.AviationSpecialist,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseResourceReserved]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.SrLogisticsManager,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseReviewUpdated]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseRouteUpdated]: [
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.QuoteApprovers,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseQuoteRejectedInternally]: [
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseQuoteCreated]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseQuoteApprovedByTxCenter]: [
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseQuoteRejectedByTxCenter]: [
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseQuoteSentToTxCenter]: [EmployeeRole.QuoteApprovers],
  [NotificationToastPermissions.ViewToastWithEventNameCaseResourceInvitationDeclined]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseQuoteApprovedInternally]: [
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.QuoteApprovers,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameCaseQuoteApprovedTxCenterProvider]: [
    EmployeeRole.LogisticsManager,
    EmployeeRole.Section16Officer,
  ],
  [NotificationToastPermissions.ViewToastWithEventNameRoutePlanningStatusUpdatedAndNewStatusWaitingForAviationApproval]:
    [EmployeeRole.AviationSpecialist],
  [NotificationToastPermissions.ViewToastWithEventNameRoutePlanningStatusUpdatedAndNewStatusBookingPending]: [
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.GroundSpecialist,
  ],
};

export const CHAT_VIEW_PERMISSIONS_BY_ROLES: Record<ChatViewPermissions, EmployeeRole[]> = {
  [ChatViewPermissions.ViewChat]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalAuditor,
  ],
};

export const PROFILE_PERMISSIONS_BY_ROLES: Record<ProfilePermissions, EmployeeRole[]> = {
  [ProfilePermissions.ProfileRead]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.CLevel,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ContractorRetrievalSurgeon,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.RegularUser,
    EmployeeRole.RemoteClinicalSpecialist,
    EmployeeRole.FinanceAuditor,
    EmployeeRole.MaintenanceOfficer,
    EmployeeRole.Section16Officer,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
    EmployeeRole.CommercialAdoptionManagement,
    EmployeeRole.CommercialAdoption,
  ],
  [ProfilePermissions.ProfileUpdate]: [
    EmployeeRole.Admin,
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.CLevel,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.AviationSpecialist,
    EmployeeRole.GroundSpecialist,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.ClinicalCoordinator,
    EmployeeRole.ClinicalSpecialist,
    EmployeeRole.RetrievalSurgeon,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalAuditor,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.ScheduleManagerCs,
    EmployeeRole.ScheduleManagerRs,
    EmployeeRole.ScheduleManagerCc,
    EmployeeRole.ScheduleManagerOms,
    EmployeeRole.Section16Officer,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.RegionalCSManagement,
  ],
};

export const BULK_ASSIGNMENT_PERMISSIONS_BY_ROLES: Record<BulkAssignmentPermissions, EmployeeRole[]> = {
  [BulkAssignmentPermissions.ViewBulkAssignment]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
  ],
  [BulkAssignmentPermissions.UpdateBulkAssignment]: [
    EmployeeRole.SuperAdmin,
    EmployeeRole.TechSupportAdmin,
    EmployeeRole.Admin,
    EmployeeRole.Section16Officer,
    EmployeeRole.QuoteApprovers,
    EmployeeRole.LogisticsTeamManagement,
    EmployeeRole.SrLogisticsManager,
    EmployeeRole.LogisticsManager,
    EmployeeRole.OrganLeadership,
    EmployeeRole.OrganManagementSpecialist,
    EmployeeRole.OrganManagement,
    EmployeeRole.ClinicalAffairsLeadership,
    EmployeeRole.NationalCSManagement,
    EmployeeRole.ClinicalCoordinatorManagement,
    EmployeeRole.ClinicalLead,
    EmployeeRole.ClinicalCoordinator,
  ],
};

export const BASE_MULTI_CASE_SUGGEST_ROLES = [
  EmployeeRole.TechSupportAdmin,
  EmployeeRole.SuperAdmin,
  EmployeeRole.Admin,
  EmployeeRole.Section16Officer,
  EmployeeRole.QuoteApprovers,
  EmployeeRole.LogisticsTeamManagement,
  EmployeeRole.SrLogisticsManager,
  EmployeeRole.LogisticsManager,
  EmployeeRole.AviationSpecialist,
  EmployeeRole.GroundSpecialist,
];

export const MULTI_CASE_SUGGEST_PERMISSIONS_BY_ROLES: Record<MultiCaseSuggestPermissions, EmployeeRole[]> = {
  [MultiCaseSuggestPermissions.ReadMCS]: BASE_MULTI_CASE_SUGGEST_ROLES,
  [MultiCaseSuggestPermissions.CreateCMS]: BASE_MULTI_CASE_SUGGEST_ROLES,
  [MultiCaseSuggestPermissions.ReviewsReadMCS]: BASE_MULTI_CASE_SUGGEST_ROLES,
  [MultiCaseSuggestPermissions.ReviewsCreateMCS]: BASE_MULTI_CASE_SUGGEST_ROLES,
};

const RELEASE_NOTES_ROLES = [EmployeeRole.TechSupportAdmin];

export const RELEASE_NOTES_PERMISSIONS_BY_ROLES: Record<ReleaseNotesPermissions, EmployeeRole[]> = {
  [ReleaseNotesPermissions.Read]: RELEASE_NOTES_ROLES,
  [ReleaseNotesPermissions.Write]: RELEASE_NOTES_ROLES,
};

export const PERMISSIONS_BY_ROLES: Record<PermissionType, EmployeeRole[]> = {
  ...ROUTE_BUILDER_PERMISSIONS_BY_ROLES,
  ...CASE_DETAILS_PERMISSIONS_BY_ROLES,
  ...CASE_BOARD_PERMISSIONS_BY_ROLES,
  ...ARCHIVE_CASES_PERMISSIONS_BY_ROLES,
  ...ADMIN_PERMISSIONS_BY_ROLES,
  ...SCHEDULER_PERMISSIONS_BY_ROLES,
  ...INVOICE_PERMISSIONS_BY_ROLES,
  ...NOTIFICATION_TOAST_PERMISSIONS_BY_ROLES,
  ...CHAT_VIEW_PERMISSIONS_BY_ROLES,
  ...PROFILE_PERMISSIONS_BY_ROLES,
  ...BULK_ASSIGNMENT_PERMISSIONS_BY_ROLES,
  ...MULTI_CASE_SUGGEST_PERMISSIONS_BY_ROLES,
  ...RELEASE_NOTES_PERMISSIONS_BY_ROLES,
};
